import { ImagePlus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useLocation } from "react-router";
import { useIch, useRueckmeldungSenden } from "../api/abfragen";
import { ApiFehler, fehlerText } from "../api/client";
import type { Ich } from "../api/typen";
import { Knopf } from "../design/Knopf";
import { Fehlertext } from "../design/Leer";
import { Sheet } from "../design/Sheet";
import { Textfeld } from "../design/Feld";
import { SICHERN_GRENZE_MS, useSichern } from "../design/sichern";
import { useToast } from "../design/toastKontext";
import { darf } from "../welt/darf";
import { useWelt } from "../welt/einstellungen";
import { BildFehler, bildVerkleinern } from "./rueckmeldung/bild";
import { entwurfBrief, entwurfGesendet, entwurfKennung, entwurfLesen, entwurfSetzen } from "./rueckmeldung/entwurf";
import { briefHinter, rueckmeldungFormular, rueckmeldungKontext } from "./rueckmeldung/kontext";
import { RM, satz } from "./rueckmeldung/satzquelle";

export type RueckmeldungModus = "rueckmeldung" | "begruessung";

/** Höchstzahl Zeichen, in Codepunkten wie der Server (`[...text].length`, ein Emoji zählt einmal; Vertragsergänzung D-668);
 *  der Zähler erscheint ab `ZAEHLER_AB`. Kein natives `maxLength`: das zählt UTF-16-Einheiten (Muster `FristNeuBlatt.tsx`).
 *  R1: der Browser schickt jeden Zeilenumbruch im multipart als CRLF (zwei Codepunkte beim Server), darum zählt er doppelt;
 *  sonst kämen 2.000 Zeichen mit Umbrüchen als mehr als 2.000 an (422 RM31). Zurück auf einfach (`[...t].length`), sobald der
 *  Server CR als Umbruch zählt (Server-Folgewelle, R2 Beta-W O1). */
export const TEXT_MAX = 2000;
export const ZAEHLER_AB = 1800;
/** D-603, entscheide C5: mit Bildschirmfoto 30 s, sonst die 10 s aller Blätter. */
export const GRENZE_MIT_BILD_MS = 30_000;

const laenge = (t: string) => [...t].length + (t.match(/\n/g)?.length ?? 0);
function kuerzen(t: string): string {
  if (laenge(t) <= TEXT_MAX) return t;
  let n = 0;
  let aus = "";
  for (const c of t) {
    const gewicht = c === "\n" ? 2 : 1;
    if (n + gewicht > TEXT_MAX) break;
    n += gewicht;
    aus += c;
  }
  return aus;
}

/** Satz zu einem gescheiterten Senden: 429 RM36, ohne Netz oder nach der Zeitgrenze RM38, sonst der Satz des Servers wörtlich
 *  (RM30 bis RM37, 503 „Gerade ist viel los …“, Liste RM39). */
function sendenFehler(fehler: unknown): string {
  if (fehler instanceof ApiFehler && fehler.status === 429) return RM["rueckmeldung.fehler.viele"];
  return fehlerText(fehler);
}

/** W82 Beta-W (D-637, D-658 F2 = A): ein Blatt mit zwei Modi. „rueckmeldung“: kurzer Text, optional ein Bildschirmfoto aus
 *  einer Datei (im Browser verkleinert), die App trägt Version, Ansicht und Darstellung selbst ein. „Senden“ steht an der
 *  Stelle von „Sichern“, ein Tipp ist genau eine Anfrage; bis zur Antwort schließt nichts das Blatt (D-089), nach 10 s (mit
 *  Bild 30 s) ohne Antwort „Keine Verbindung …“, Text und Bild bleiben, die Wiederholung geht mit derselben Kennung. Danach
 *  zu und der Hinweis der App: Gaist-Satz (RM24) oder stiller Satz (RM25) nach `darf("satzAntwort")`. „begruessung“: die
 *  Beta-Begrüßung einmal je Konto und Gerät (`BetaBegruessung.tsx`). Ein Blatt (`Sheet`) für beide (Inventar 50). */
export function RueckmeldungBlatt(props: { offen: boolean; modus: RueckmeldungModus; onSchliessen: () => void; fokusNachher?: () => HTMLElement | null }) {
  const { data: ich } = useIch();
  // `ich` kann ohne Nutzer antworten (Unit-Tests der Seiten, W82-2 N1 in Profil.tsx)
  const nutzer = ich?.nutzer?.id ?? null;
  const ort = useLocation();
  const [welt] = useWelt();
  const toast = useToast();
  const senden = useRueckmeldungSenden();
  const { laeuft, sichern } = useSichern(props.offen);
  const [text, setText] = useState(() => entwurfLesen(nutzer).text);
  const [bild, setBild] = useState<Blob | null>(() => entwurfLesen(nutzer).bild);
  const [bereitet, setBereitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // ein Tipp, eine Anfrage: auch ein zweiter Klick, ein zweites Enter oder eine gehaltene Taste vor dem nächsten Bild sendet nichts
  const gesendet = useRef(false);
  const feld = useRef<HTMLTextAreaElement>(null);
  const datei = useRef<HTMLInputElement>(null);
  const anhaengen = useRef<HTMLButtonElement>(null);
  const entfernen = useRef<HTMLButtonElement>(null);
  const vorschauBild = useRef<HTMLImageElement>(null);
  const nachWahl = useRef(false);
  // W82 Web-10c (W10C-3, PA:470, wie iOS): ein Bild gilt nur für das Konto und die Wahl, für die es verkleinert wurde; die
  // Generation steigt mit jeder Wahl, beim Entfernen, beim Schließen des Blatts und beim Kontowechsel
  const generation = useRef(0);
  const nutzerJetzt = useRef(nutzer);
  const qc = useQueryClient();
  useEffect(() => {
    nutzerJetzt.current = nutzer;
    generation.current += 1;
  }, [nutzer]);
  useEffect(() => {
    if (!props.offen) generation.current += 1;
  }, [props.offen]);
  // ausgehängt (etwa beim Abmelden): ein spätes Bild schriebe sonst in den globalen Entwurf
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  // jedes Öffnen zeigt den Entwurf (gesetzt beim Rendern, damit kein alter Stand aufblitzt); alte Fehler fallen weg
  const [warOffen, setWarOffen] = useState(props.offen);
  if (warOffen !== props.offen) {
    setWarOffen(props.offen);
    if (props.offen) {
      const e = entwurfLesen(nutzer);
      setText(e.text);
      setBild(e.bild);
      setFehler(null);
    } else setBereitet(false);
  }

  // W82 Web-5h-N2 a7 (H1): die Kennung entsteht beim ersten Öffnen im Modus „rueckmeldung“, nicht beim Einhängen des Menüs
  const kennungNoetig = props.offen && props.modus === "rueckmeldung";
  useEffect(() => {
    if (kennungNoetig) entwurfKennung(nutzer);
  }, [kennungNoetig, nutzer]);

  const vorschau = props.offen && bild !== null;
  // Vorschau über eine Objekt-URL, nur solange das Blatt offen ist; freigegeben beim Entfernen und beim Schließen
  useEffect(() => {
    const img = vorschauBild.current;
    if (!vorschau || !bild || !img) return;
    const url = URL.createObjectURL(bild);
    img.src = url;
    return () => {
      img.removeAttribute("src");
      URL.revokeObjectURL(url);
    };
  }, [bild, vorschau]);
  // nach der Wahl liegt der Fokus auf „Entfernen“ (der Knopf „Bildschirmfoto anhängen“ ist dann weg)
  useEffect(() => {
    if (!vorschau || !nachWahl.current) return;
    nachWahl.current = false;
    entfernen.current?.focus({ preventScroll: true });
  }, [vorschau, bild]);

  function textAendern(wert: string) {
    const neu = kuerzen(wert);
    setText(neu);
    entwurfSetzen(nutzer, { text: neu });
    if (fehler) setFehler(null);
  }

  async function gewaehlt(e: ChangeEvent<HTMLInputElement>) {
    const d = e.target.files?.[0];
    e.target.value = "";
    if (!d) return;
    setBereitet(true);
    setFehler(null);
    const gen = ++generation.current;
    const fuer = nutzer;
    // nach dem Warten: gilt die Wahl noch (gleiches Konto, keine neuere Wahl, Blatt nicht geschlossen)? Sonst verworfen, der
    // Entwurf des anderen Kontos bleibt unberührt
    const gilt = () => generation.current === gen && nutzerJetzt.current === fuer && (qc.getQueryData<Ich | null>(["ich"])?.nutzer?.id ?? null) === fuer;
    try {
      const klein = await bildVerkleinern(d);
      if (!gilt()) return;
      nachWahl.current = true;
      setBild(klein);
      entwurfSetzen(nutzer, { bild: klein });
    } catch (f) {
      if (!gilt()) return;
      setFehler(f instanceof BildFehler && f.art === "gross" ? RM["rueckmeldung.fehler.gross"] : RM["rueckmeldung.fehler.unlesbar"]);
    } finally {
      // eine neuere Wahl setzt es selbst zurück, das Schließen beim Rendern; nach einem Kontowechsel hier
      if (gilt() || nutzerJetzt.current !== fuer) setBereitet(false);
    }
  }

  function bildEntfernen() {
    generation.current += 1;
    setBild(null);
    entwurfSetzen(nutzer, { bild: null });
    // der Knopf „Bildschirmfoto anhängen“ steht erst nach dem Rendern wieder da
    requestAnimationFrame(() => anhaengen.current?.focus({ preventScroll: true }));
  }

  function abschicken() {
    if (gesendet.current || laeuft || bereitet) return;
    if (!text.trim()) {
      setFehler(RM["rueckmeldung.fehler.leer"]);
      feld.current?.focus();
      return;
    }
    gesendet.current = true;
    setFehler(null);
    const kennung = entwurfKennung(nutzer);
    // W82 Beta-W2: der Brief hinter dem Blatt beim ersten Sendeversuch dieser Kennung; Wiederholungen schicken denselben
    const dokumentId = entwurfBrief(nutzer, briefHinter(ort.pathname));
    const formular = rueckmeldungFormular({ kennung, text, kontext: rueckmeldungKontext(ort.pathname, welt), bild, dokumentId });
    // BS30 und C5: nach 10 s (mit Bild 30 s) ohne Antwort bricht `useSichern` ab (onError mit dem Netz-Satz, Knöpfe frei)
    sichern(
      (signal) =>
        senden.mutateAsync(
          { formular, signal },
          {
            onSuccess: () => {
              entwurfGesendet(nutzer);
              setText("");
              setBild(null);
              const gaist = darf("satzAntwort", { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: false });
              toast(gaist ? RM["rueckmeldung.danke.gaist"] : RM["rueckmeldung.danke.still"]);
              props.onSchliessen();
            },
            onError: (f) => setFehler(sendenFehler(f)),
            onSettled: () => {
              gesendet.current = false;
            },
          },
        ),
      bild ? GRENZE_MIT_BILD_MS : SICHERN_GRENZE_MS,
    );
  }

  const verstanden = () => props.onSchliessen();
  const begruessung = props.modus === "begruessung";
  const zeichen = laenge(text);
  const zaehler =
    zeichen >= TEXT_MAX ? RM["rueckmeldung.feld.voll"] : zeichen >= ZAEHLER_AB ? satz(RM["rueckmeldung.feld.rest"], { rest: (TEXT_MAX - zeichen).toLocaleString("de-DE") }) : null;
  return (
    <Sheet
      offen={props.offen}
      onSchliessen={props.onSchliessen}
      gesperrt={laeuft}
      sperrText={RM["rueckmeldung.laeuft"]}
      fokusNachher={props.fokusNachher}
      // W82 Web-5h-N2 a5 (Frage 73 = A, D-684): die Begrüßung ist ein Hinweisblatt, ihr einziger Knopf ist RM70 „Verstanden“
      ohneAbbrechen={begruessung}
      // W82 Web-5h-N2b-2 (b-HB1, D-701): „Verstanden“ ist der einzige Weg hinaus; der Merker bleibt beim Öffnen (BetaBegruessung)
      nurMitKnopf={begruessung}
      titel={begruessung ? RM["beta.begruessung.titel"] : RM["rueckmeldung.titel"]}
      rechts={
        begruessung ? undefined : (
          <button
            type="button"
            disabled={laeuft || bereitet}
            onClick={abschicken}
            data-testid="rueckmeldung.senden"
            className="t-headline min-h-(--touch) whitespace-nowrap text-pg-accent-text disabled:text-pg-text-3"
          >
            {/* W82 Web-5h-N2 a4 (Frage 75 = A, D-689): immer RM20, beim Senden nur gesperrt; RM21 steht allein in der Sperrzeile */}
            {RM["rueckmeldung.senden"]}
          </button>
        )
      }
    >
      {begruessung ? (
        <div className="beta-begruessung" data-testid="beta-begruessung">
          <div className="mt-2 space-y-2 px-4">
            <p className="t-body hyphens-auto">{RM["beta.begruessung.text.1"]}</p>
            <p className="t-body hyphens-auto text-pg-text-2">{RM["beta.begruessung.text.2"]}</p>
            <p className="t-body hyphens-auto text-pg-text-2">{RM["beta.begruessung.text.3"]}</p>
          </div>
          {/* gesperrt wie das Blatt (Regel W51 R2 zu A2); die Begrüßung sendet nichts, `laeuft` bleibt hier falsch */}
          <Knopf gross breit className="mt-6" autoFocus disabled={laeuft} onClick={verstanden}>
            {RM["beta.begruessung.knopf"]}
          </Knopf>
        </div>
      ) : (
        <div className="rueckmeldung-blatt" data-testid="rueckmeldung-blatt">
          <p className="t-body mt-2 hyphens-auto px-4 text-pg-text-2">{RM["rueckmeldung.einleitung"]}</p>
          <ul className="gruppe mt-4 overflow-clip rounded-group bg-pg-surface">
            <Textfeld
              ref={feld}
              label={RM["rueckmeldung.feld"]}
              value={text}
              rows={4}
              autoFocus
              readOnly={laeuft}
              onChange={(e) => textAendern(e.target.value)}
              aria-invalid={fehler === RM["rueckmeldung.fehler.leer"] || undefined}
              // Zähler nur als Beschreibung des Felds, ohne Ansage je Taste
              hinweis={zaehler && <span data-testid="rueckmeldung.zaehler">{zaehler}</span>}
            />
          </ul>
          {fehler && <Fehlertext fehler={new Error(fehler)} />}
          <div className="rueckmeldung-bild mt-4 flex flex-col items-start gap-2 px-4">
            {vorschau ? (
              <div className="flex w-full flex-wrap items-center gap-3" data-testid="rueckmeldung.vorschau">
                <img ref={vorschauBild} alt={RM["rueckmeldung.bild.name"]} className="size-(--leer-kachel) shrink-0 rounded-md object-cover shadow-karte" />
                <span className="t-body min-w-0 flex-1 hyphens-auto">{RM["rueckmeldung.bild.gewaehlt"]}</span>
                <Knopf ref={entfernen} art="text" disabled={laeuft} onClick={bildEntfernen}>
                  {RM["rueckmeldung.bild.entfernen"]}
                </Knopf>
              </div>
            ) : (
              <Knopf ref={anhaengen} art="sekundaer" icon={ImagePlus} disabled={laeuft || bereitet} onClick={() => datei.current?.click()} className="hyphens-auto">
                {RM["rueckmeldung.bild.knopf"]}
              </Knopf>
            )}
            {/* versteckt; der Knopf darüber öffnet die Auswahl (Muster Erfassen.tsx) */}
            <input ref={datei} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => void gewaehlt(e)} data-testid="rueckmeldung.datei" />
            <p className="t-footnote hyphens-auto text-pg-text-2">{RM["rueckmeldung.bild.warnung"]}</p>
            <p className="t-footnote hyphens-auto text-pg-text-2">{RM["rueckmeldung.bild.weg.web"]}</p>
          </div>
          <p className="t-footnote mt-6 hyphens-auto px-4 text-pg-text-2">{RM["rueckmeldung.angaben.web"]}</p>
        </div>
      )}
    </Sheet>
  );
}
